import React, { useCallback, useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Badge, Box, Button, Checkbox, Chip, Dialog,
  DialogActions, DialogContent, DialogTitle, FormControlLabel, IconButton, LinearProgress, Stack, TextField,
  ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { Add, Close, DeleteOutline, EditOutlined, ExpandMore, MailOutline, PushPinOutlined, Refresh, UploadFile } from '@mui/icons-material';
import { apiService, API_CONFIG, API_ENDPOINTS } from '../../config/api';
import type { DashboardUpdate } from '../../types';
import DeveloperUpdateEmailDialog from './DeveloperUpdateEmailDialog';

const emptyForm = { title: '', body: '', imageUrl: '', pinned: false, isPublished: true };
const dateLabel = (value?: string | null) => value && Number.isFinite(Date.parse(value))
  ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '';

export default function DeveloperUpdatesMailbox({ editable = false }: { editable?: boolean }) {
  const [items, setItems] = useState<DashboardUpdate[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [expanded, setExpanded] = useState<string | false>(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<DashboardUpdate | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [editorError, setEditorError] = useState('');
  const [deleting, setDeleting] = useState<DashboardUpdate | null>(null);
  const [emailUpdate, setEmailUpdate] = useState<DashboardUpdate | null>(null);
  const unread = items.filter(item => item.isPublished && !item.isRead).length;

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await apiService.get(`${API_ENDPOINTS.DASHBOARD_UPDATES.LIST}${editable ? '?includeAll=true' : ''}`);
      setItems(result.items || []); setError('');
    } catch (_) { setError('Could not load developer updates. Please refresh.'); }
    finally { setLoading(false); }
  }, [editable]);
  useEffect(() => {
    void load();
    const refresh = () => { if (document.visibilityState === 'visible') void load(); };
    const timer = window.setInterval(refresh, 120000);
    window.addEventListener('focus', refresh);
    return () => { window.clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, [load]);

  const markRead = async (updates: DashboardUpdate[]) => {
    const unreadItems = updates.filter(item => item.isPublished && !item.isRead && item.revision);
    try {
      for (let offset = 0; offset < unreadItems.length; offset += 200) {
        const batch = unreadItems.slice(offset, offset + 200).map(({ id, revision }) => ({ id, revision }));
        await apiService.post(`${API_ENDPOINTS.DASHBOARD_UPDATES.LIST}/read`, { items: batch });
        setItems(current => current.map(item => batch.some(read => read.id === item.id && read.revision === item.revision) ? { ...item, isRead: true } : item));
      }
    } catch (_) { setError('Could not save your read status. Please try again.'); }
  };
  const edit = (update: DashboardUpdate | null) => {
    setEditing(update); setEditorError('');
    setForm(update ? { title: update.title, body: update.body, imageUrl: update.imageUrl || '', pinned: update.pinned, isPublished: update.isPublished } : emptyForm);
    setEditorOpen(true);
  };
  const save = async () => {
    if (!form.title.trim() || !form.body.trim()) { setEditorError('Enter a title and message.'); return; }
    setSaving(true); setEditorError('');
    try {
      const payload = { ...form, title: form.title.trim(), body: form.body.trim(), imageUrl: form.imageUrl || null };
      if (editing) await apiService.patch(API_ENDPOINTS.DASHBOARD_UPDATES.DETAIL(editing.id), payload);
      else await apiService.post(API_ENDPOINTS.DASHBOARD_UPDATES.LIST, payload);
      setEditorOpen(false); await load();
    } catch (e: any) { setEditorError(e.message || 'Could not save this update.'); }
    finally { setSaving(false); }
  };
  const upload = async (file?: File) => {
    if (!file) return;
    if (!file.type.startsWith('image/') || file.size > 5 * 1024 * 1024) { setEditorError('Choose an image smaller than 5 MB.'); return; }
    setSaving(true); setEditorError('');
    try {
      const token = await apiService.getAuthToken();
      const body = new FormData(); body.append('image', file);
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_ENDPOINTS.DASHBOARD_UPDATES.UPLOAD_IMAGE}`,
        { method: 'POST', headers: token ? { Authorization: `Bearer ${token}` } : {}, body });
      if (!response.ok) throw new Error('Could not upload the image.');
      const result = await response.json(); setForm(current => ({ ...current, imageUrl: result.imageUrl }));
    } catch (e: any) { setEditorError(e.message || 'Could not upload the image.'); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!deleting) return;
    setSaving(true);
    try { await apiService.delete(API_ENDPOINTS.DASHBOARD_UPDATES.DETAIL(deleting.id)); setDeleting(null); await load(); }
    catch (_) { setError('Could not delete this update.'); setDeleting(null); }
    finally { setSaving(false); }
  };
  const visible = items.filter(item => (filter !== 'unread' || (item.isPublished && !item.isRead) || expanded === item.id) &&
    `${item.title} ${item.body}`.toLowerCase().includes(search.toLowerCase()));

  return <>
    <Tooltip title={error || 'Developer updates'}>
      <Button color="primary" aria-label={`Developer updates (${unread} unread)`} onClick={() => { setOpen(true); void load(); }}
        sx={{ borderRadius: 2, minWidth: 44, px: { xs: 1, sm: 1.5 }, gap: 1 }}>
        <Badge badgeContent={error ? '!' : unread} color={error ? 'warning' : 'error'} max={99}><MailOutline /></Badge>
        <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Updates</Box>
      </Button>
    </Tooltip>
    <Dialog open={open} onClose={() => setOpen(false)} fullWidth maxWidth="md" aria-labelledby="developer-updates-title" PaperProps={{ sx: { borderRadius: 3, height: 'min(780px, 88vh)' } }}>
      <DialogTitle id="developer-updates-title" sx={{ pr: 7 }}>Developer updates
        <Typography variant="body2" color="text.secondary" sx={{ mt: .5 }}>What's new in StudySync, all in one place.</Typography>
        <IconButton aria-label="Close developer updates" onClick={() => setOpen(false)} sx={{ position: 'absolute', top: 14, right: 14 }}><Close /></IconButton>
      </DialogTitle>
      <Box sx={{ px: 3, pb: 2 }}>
        <Stack direction="row" gap={1} flexWrap="wrap" alignItems="center" sx={{ mb: 1.5 }}>
          <ToggleButtonGroup size="small" exclusive value={filter} onChange={(_, next) => { if (next) { setFilter(next); setExpanded(false); } }} aria-label="Filter updates">
            <ToggleButton value="all">All updates</ToggleButton><ToggleButton value="unread">Unread ({unread})</ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          <Button size="small" disabled={!unread} onClick={() => void markRead(items)}>Mark all as read</Button>
          <Tooltip title="Refresh updates"><span><IconButton aria-label="Refresh updates" disabled={loading} onClick={() => void load()}><Refresh /></IconButton></span></Tooltip>
          {editable && <Button variant="contained" startIcon={<Add />} onClick={() => edit(null)}>New update</Button>}
        </Stack>
        <TextField fullWidth size="small" label="Search developer updates" value={search} onChange={event => setSearch(event.target.value)} />
      </Box>
      {loading && <LinearProgress aria-label="Loading developer updates" />}
      <DialogContent dividers sx={{ bgcolor: '#f7f8fa', py: 2 }}>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {!loading && visible.length === 0 && <Box sx={{ textAlign: 'center', py: 6, color: 'text.secondary' }}><MailOutline sx={{ fontSize: 40, mb: 1 }} />
          <Typography>{search ? 'No updates match your search.' : filter === 'unread' ? "You're all caught up." : 'No developer updates yet.'}</Typography></Box>}
        {visible.map(item => <Accordion key={item.id} expanded={expanded === item.id} disableGutters elevation={0} TransitionProps={{ unmountOnExit: true }}
          onChange={(_, next) => { setExpanded(next ? item.id : false); if (next) void markRead([item]); }}
          sx={{ mb: 1.25, border: '1px solid', borderColor: item.isPublished && !item.isRead ? '#dfb8b8' : 'divider', borderRadius: '12px !important', '&::before': { display: 'none' } }}>
          <AccordionSummary expandIcon={<ExpandMore />} aria-controls={`update-${item.id}-content`} id={`update-${item.id}-heading`}>
            <Stack sx={{ width: '100%', pr: 1 }} gap={.75}>
              <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                {item.isPublished && !item.isRead && <Box aria-label="Unread" sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', flexShrink: 0 }} />}
                <Typography sx={{ fontWeight: item.isRead ? 500 : 650, overflowWrap: 'anywhere' }}>{item.title}</Typography>
                {item.pinned && <PushPinOutlined fontSize="small" color="action" aria-label="Pinned" />}
                {!item.isPublished && <Chip size="small" label="Draft" />}
              </Stack>
              <Typography variant="caption" color="text.secondary">StudySync team · {dateLabel(item.updatedAt || item.createdAt)}</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails id={`update-${item.id}-content`}>
            {item.imageUrl && <Box component="img" src={item.imageUrl} alt={item.title} sx={{ width: '100%', maxHeight: 320, objectFit: 'contain', borderRadius: 2, mb: 2 }} />}
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', lineHeight: 1.8 }}>{item.body}</Typography>
            {editable && <Stack direction="row" gap={1} flexWrap="wrap" sx={{ mt: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
              <Button size="small" startIcon={<EditOutlined />} onClick={() => edit(item)}>Edit</Button>
              <Button size="small" startIcon={<MailOutline />} disabled={!item.isPublished} onClick={() => setEmailUpdate(item)}>Email researchers</Button>
              <Box sx={{ flex: 1 }} /><IconButton aria-label={`Delete ${item.title}`} size="small" onClick={() => setDeleting(item)}><DeleteOutline fontSize="small" /></IconButton>
            </Stack>}
          </AccordionDetails>
        </Accordion>)}
      </DialogContent>
    </Dialog>
    {editable && <Dialog open={editorOpen} onClose={saving ? undefined : () => setEditorOpen(false)} fullWidth maxWidth="sm" aria-labelledby="edit-update-title">
      <DialogTitle id="edit-update-title">{editing ? 'Edit developer update' : 'New developer update'}</DialogTitle>
      <DialogContent dividers><Stack gap={2}>
        {editorError && <Alert severity="error">{editorError}</Alert>}
        <TextField label="Title" value={form.title} required fullWidth inputProps={{ maxLength: 48 }} onChange={event => setForm({ ...form, title: event.target.value })} helperText={`${form.title.length}/48 characters`} />
        <TextField label="Message" multiline minRows={6} required fullWidth inputProps={{ maxLength: 4000 }} value={form.body} onChange={event => setForm({ ...form, body: event.target.value })} />
        {form.imageUrl && <Box component="img" src={form.imageUrl} alt="Update preview" sx={{ maxWidth: '100%', maxHeight: 180, objectFit: 'contain' }} />}
        <Stack direction="row" gap={1}>
          <Button component="label" variant="outlined" startIcon={<UploadFile />} disabled={saving}>{form.imageUrl ? 'Replace image' : 'Add image'}
            <input type="file" accept="image/*" hidden onChange={event => { void upload(event.target.files?.[0]); event.target.value = ''; }} />
          </Button>
          {form.imageUrl && <Button disabled={saving} onClick={() => setForm({ ...form, imageUrl: '' })}>Remove image</Button>}
        </Stack>
        <FormControlLabel label="Pin this update" control={<Checkbox checked={form.pinned} onChange={event => setForm({ ...form, pinned: event.target.checked })} />} />
        <FormControlLabel label="Publish in the mailbox" control={<Checkbox checked={form.isPublished} onChange={event => setForm({ ...form, isPublished: event.target.checked })} />} />
        <Typography variant="caption" color="text.secondary">Publishing adds the update to the mailbox. Email delivery is a separate action.</Typography>
      </Stack></DialogContent>
      <DialogActions sx={{ p: 2 }}><Button disabled={saving} onClick={() => setEditorOpen(false)}>Cancel</Button><Button variant="contained" disabled={saving} onClick={save}>{form.isPublished ? 'Save and publish' : 'Save draft'}</Button></DialogActions>
    </Dialog>}
    <Dialog open={Boolean(deleting)} onClose={saving ? undefined : () => setDeleting(null)} aria-labelledby="delete-update-title">
      <DialogTitle id="delete-update-title">Delete this update?</DialogTitle>
      <DialogContent><Typography>{deleting?.title}</Typography><Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>This removes it from the mailbox. Emails already sent cannot be recalled.</Typography></DialogContent>
      <DialogActions><Button disabled={saving} onClick={() => setDeleting(null)}>Cancel</Button><Button color="error" disabled={saving} onClick={remove}>Delete</Button></DialogActions>
    </Dialog>
    {editable && emailUpdate && <DeveloperUpdateEmailDialog key={`${emailUpdate.id}:${emailUpdate.revision}`} update={emailUpdate} onClose={() => setEmailUpdate(null)} />}
  </>;
}
